/**
 * [CurveBeamAI] BoneVue segmentation workflow preset.
 *
 * Configures the viewer for the BMD segmentation workflow: loads CT from
 * HealthImaging and SEG from S3 presigned URL, displays Bone VOI with
 * NEAREST interpolation so labelmap edges align with CT voxels, and
 * provides a custom right panel with OHIF native labelmap tools + save button.
 *
 * Panel customizations:
 *   - tableMode 'collapsed' hides "Add segmentation" when a SEG already exists
 *   - showAddSegment false hides "Add segment" within an existing segmentation
 *   - disableEditing is NOT set so brush/eraser tools remain functional
 */

import { BONEVUE_SEG_STORE } from '@CurveBeamAI/services/bonevue/segmentation/segStoreContract';
import { WorkflowPreset } from '../../types';

export const bonevueSegmentationPreset: WorkflowPreset = {
  id: 'bonevue-segmentation',
  label: 'BoneVue Segmentation',
  toolbar: {
    hiddenButtonIds: [
      'MeasurementTools',
      'Probe',
      'Angle',
      'CobbAngle',
      'CalibrationLine',
      'AdvancedMagnify',
    ],
  },
  panels: {
    right: ['@curvebeamai/ohif-extension.panelModule.bonevueSegmentationPanel'],
    rightPanelClosed: false,
  },
  header: {
    showPatientInfo: true,
    showReturnButton: false,
  },
  customizations: {
    'header.hideUndoRedo': true,
    'panelSegmentation.tableMode': { $set: 'collapsed' },
    'panelSegmentation.showAddSegment': { $set: false },
    // [CurveBeamAI] The BoneVue pipeline reads a binary, uncompressed SEG, and OHIF
    // 3.13 defaults SEG export to Label Map (SOP class ...66.7) encoded as RLE
    // Lossless — which produced a file it could not read.
    //
    // The aws-healthimaging data source pins the same values, but a per-data-source
    // override only applies when THAT source resolves: `generateSegmentation` reads
    // `options.dataSource` else the active source, so a call naming another source
    // (or a change of active source) would silently fall back to the Label Map
    // default. Setting it here covers every resolution path, and belongs to the
    // workflow because the constraint is the pipeline consuming the file rather
    // than the backend storing it.
    'segmentation.store.defaultMode': { $set: BONEVUE_SEG_STORE.defaultMode },
    'segmentation.store.transferSyntaxUID': { $set: BONEVUE_SEG_STORE.transferSyntaxUID },
  },
  dataFilters: {
    skipSeriesFilter: true,
  },
  imageInterpolation: 'NEAREST',
  defaultVoi: { windowWidth: 2500, windowCenter: 480 },
};
